import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  eachDayOfInterval,
  startOfWeek,
  addDays,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

// Importar locales do date-fns para versão 2.x
import { enUS } from 'date-fns/locale';
import { ptBR } from 'date-fns/locale';
import { pt } from 'date-fns/locale';
import { es } from 'date-fns/locale';

/**
 * Obter o locale do usuário atual baseado no Vue i18n, configuração global ou navegador
 * @param {Object} vueInstance - Instância do Vue
 * @returns {string} Código do locale
 */
export const getCurrentUserLocale = (vueInstance = null) => {
  // Tentar obter de diferentes fontes
  let locale = null;

  // Primeiro, tentar obter do Vue i18n
  if (vueInstance && vueInstance.$i18n) {
    locale = vueInstance.$i18n.locale;
  }

  // Segundo, tentar obter da configuração global
  if (!locale && typeof window !== 'undefined' && window.chatwootConfig) {
    locale = window.chatwootConfig.selectedLocale;
  }

  // Terceiro, tentar obter do navegador
  if (!locale && typeof navigator !== 'undefined') {
    locale = navigator.language;
  }

  // Fallback para 'en' se nenhum locale for encontrado
  return locale || 'en';
};

/**
 * Mapear código de locale para objeto de locale do date-fns
 * @param {string} localeCode - Código do locale
 * @returns {Object} Objeto de locale do date-fns
 */
export const getDateFnsLocale = localeCode => {
  const localeMap = {
    'pt-BR': ptBR,
    pt_BR: ptBR,
    pt: pt,
    es: es,
    en: enUS,
    'en-US': enUS,
  };

  return localeMap[localeCode] || enUS;
};

/**
 * Constrói o objeto `lang` completo para o vue2-datepicker a partir do locale do usuário.
 * O `formatLocale` segue a estrutura do `date-format-parse` (arrays de months/weekdays),
 * necessária para que o vue2-datepicker 3.x localize corretamente o mês no header.
 * @param {string} localeCode - Código do locale
 * @returns {Object} Objeto `lang` compatível com vue2-datepicker 3.x
 */
export const getDatePickerLang = localeCode => {
  const locale = getDateFnsLocale(localeCode);

  const sunday = startOfWeek(new Date(1970, 0, 4), { weekStartsOn: 0 });
  const weekdaysShort = eachDayOfInterval({
    start: sunday,
    end: addDays(sunday, 6),
  }).map(day => {
    const short = format(day, 'EEE', { locale }).slice(0, 3);
    return short.charAt(0).toUpperCase() + short.slice(1);
  });

  const year = new Date().getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => {
    const name = format(new Date(year, i, 1), 'MMMM', { locale });
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
  const monthsShort = Array.from({ length: 12 }, (_, i) => {
    const name = format(new Date(year, i, 1), 'MMM', { locale });
    return name.charAt(0).toUpperCase() + name.slice(1);
  });

  const dfpLocale = {
    months,
    monthsShort,
    weekdays: weekdaysShort,
    weekdaysShort,
    weekdaysMin: weekdaysShort,
    firstDayOfWeek: 0,
    firstWeekContainsDate: 1,
  };

  return {
    formatLocale: dfpLocale,
    days: weekdaysShort,
    months: monthsShort,
    monthsLong: months,
    yearFormat: 'YYYY',
    monthFormat: 'MMMM',
    monthBeforeYear: false,
  };
};

const DATE_PICKER_TOKEN_BY_PART = {
  year: 'YYYY',
  month: 'MM',
  day: 'DD',
};

// Locale padrão da aplicação (config/application.rb). Usado quando o código recebido não é
// reconhecido pelo Intl.
const DATE_PICKER_FALLBACK_LOCALE = 'pt-BR';

/**
 * Resolve o código para uma tag que o Intl reconhece, ou cai no padrão da aplicação.
 *
 * Existe porque o Intl NÃO lança para tag desconhecida mas sintaticamente válida ('not-a-locale'
 * tem essa forma): ele silenciosamente usa o locale padrão do AMBIENTE. No navegador isso é o
 * locale do usuário — ou seja, sem esta função uma conta com locale desconhecido voltaria a
 * exibir a data no formato do navegador, que é exatamente o bug da issue #587.
 *
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string} Tag BCP-47 suportada, ou o padrão da aplicação
 */
const resolveDatePickerLocale = localeCode => {
  const tag = String(localeCode || '').replace('_', '-');
  if (!tag) return DATE_PICKER_FALLBACK_LOCALE;

  try {
    const [supported] = Intl.DateTimeFormat.supportedLocalesOf([tag]);
    return supported || DATE_PICKER_FALLBACK_LOCALE;
  } catch (error) {
    // Tag sintaticamente invalida: o supportedLocalesOf lanca RangeError.
    return DATE_PICKER_FALLBACK_LOCALE;
  }
};

/**
 * Máscara de exibição do vue2-datepicker derivada do locale da CONTA.
 *
 * O `lang` acima localiza só os NOMES de mês e de dia da semana; ele não carrega máscara
 * nenhuma, então não influencia a ordem dia/mês. Sem a prop `format` o vue2-datepicker cai
 * no default fixo dele (`YYYY-MM-DD HH:mm:ss`), igual para toda conta e com o mês antes do
 * dia — que é o formato "norte-americano" relatado na issue #587.
 *
 * A ordem sai do `Intl.formatToParts`, e não de um mapa locale->máscara escrito à mão, para
 * um locale novo nascer certo. Os tokens são os do `date-format-parse` (o formatador que o
 * vue2-datepicker usa), NÃO os do date-fns — por isso não dá para reaproveitar o
 * `getIntlDateFormatForLocale` do DatePickerHelper, que além disso lê o locale do NAVEGADOR.
 *
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @param {Object} [options]
 * @param {boolean} [options.withTime=true] - Anexa a parte de hora à máscara
 * @returns {string} Máscara no formato do date-format-parse (ex.: 'DD/MM/YYYY HH:mm')
 */
export const getDatePickerFormat = (localeCode, { withTime = true } = {}) => {
  const tag = resolveDatePickerLocale(localeCode);
  // Ordem do locale padrão da aplicação, caso o Intl falhe mesmo com uma tag suportada.
  let datePart = 'DD/MM/YYYY';
  let timePart = 'HH:mm';

  try {
    // 2020-03-04: dia, mês e ano com valores distintos, para não confundir as partes.
    const parts = new Intl.DateTimeFormat(tag, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    }).formatToParts(new Date(Date.UTC(2020, 2, 4)));

    const mask = parts
      .map(({ type, value }) => {
        if (DATE_PICKER_TOKEN_BY_PART[type])
          return DATE_PICKER_TOKEN_BY_PART[type];
        // Literal com letra (o "de" de alguns locales, o 年 do japonês) seria lido como
        // token pelo date-format-parse; [] é o escape dele.
        return /[a-zA-Z]/.test(value) ? `[${value}]` : value;
      })
      .join('');

    // Só aceita a máscara derivada se ela trouxe as três partes; senão fica o padrão.
    if (mask.includes('YYYY') && mask.includes('MM') && mask.includes('DD')) {
      datePart = mask;
    }

    const { hour12 } = new Intl.DateTimeFormat(tag, {
      hour: 'numeric',
    }).resolvedOptions();
    timePart = hour12 ? 'hh:mm A' : 'HH:mm';
  } catch (error) {
    // Locale desconhecido pelo Intl: mantém o padrão acima em vez de quebrar o campo.
  }

  return withTime ? `${datePart} ${timePart}` : datePart;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number|Date} time - Unix timestamp or Date.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @param {string} [locale='en'] - Locale for formatting.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a', locale = 'en') => {
  const unixTime = time instanceof Date ? time : fromUnixTime(time);
  const dateLocale = getDateFnsLocale(locale);
  return format(unixTime, dateFormat, { locale: dateLocale });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @param {string} [locale='en'] - Locale for formatting.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (
  time,
  dateFormat = 'MMM d, yyyy',
  locale = 'en',
  timezone = null
) => {
  const rawMessageTime = fromUnixTime(time);
  const rawNow = new Date();
  const messageTime = timezone
    ? utcToZonedTime(rawMessageTime, timezone)
    : rawMessageTime;
  const now = timezone ? utcToZonedTime(rawNow, timezone) : rawNow;
  const dateLocale = getDateFnsLocale(locale);
  const messageDate = format(messageTime, dateFormat, { locale: dateLocale });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a', { locale: dateLocale });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @param {string} [locale='en'] - Locale for formatting.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale = 'en') => {
  const unixTime = fromUnixTime(time);
  const dateLocale = getDateFnsLocale(locale);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: dateLocale,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @param {string} [locale='en'] - Locale for formatting.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy', locale = 'en') => {
  const unixTime = fromUnixTime(time);
  const dateLocale = getDateFnsLocale(locale);
  return format(unixTime, df, { locale: dateLocale });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};
